import { Logger } from '../../logger/logger';
import { HttpClient } from '../http/http-client/http-client';
import { HttpRequestInit } from '../http/http-request/http-request';
import { HttpResponse } from '../http/http-response/http-response';
import { CXoneAuthBase } from './cxone-auth-base';
import { StorageKeys } from '../../constants/storage-key'
import { SecurityHelper } from '../../util/security-helper';
import { HttpUtilService } from '../http/http-util-service';
import { LocalStorageHelper } from '../../util/storage-helper-local';
import {AuthSettings, AuthToken, AuthWithCodeReq} from '@nice-ccf/common-sdk';

/**
 * Class to manage authentication mechanism
 */
export class CXoneAuthByCode extends CXoneAuthBase {
  logger: Logger = new Logger('lib.authenticate', 'lib.CXoneAuthByCode');
  private static singleton: CXoneAuthByCode;
  private securityHelper = new SecurityHelper();
  public authSettings = <AuthSettings>{};
  protected utilService: HttpUtilService = new HttpUtilService();

  /**
   * Method to create singleton object of the class
   * ```
   * @example
   * const authObject = CXoneAuthByCode.instance();
   * ```
   */
  public static get instance(): CXoneAuthByCode {
    if (!CXoneAuthByCode.singleton) {
      CXoneAuthByCode.singleton = new CXoneAuthByCode();
      CXoneAuthBase.cxoneAuth = CXoneAuthByCode.singleton;
    }
    return CXoneAuthByCode.singleton;
  }

  /**
   *
   * @param authSettings - initialize the auth settings
   * ```
   * @example
   * init({'cxoneHostname': 'https://cxone.dev.niceincontact.com', 'clientId': 'Salesforce Agent Console@inContact Inc.'})
   * ```
   */
  init(authSettings: AuthSettings): void {
    this.authSettings = authSettings;
  }

   /**
   * Method generate the Authorize url using authorize endpoint with clientId, code challenge, authMode and codeChallengeMethod.
   * This url will be use to open the login screen in page or popup window based on the display value.
   * 
   * @param displayMode - get the authmode, whether page or popup
   * @param codeChallengeMethod - 'S256'
   * @returns authUrl
   * ```
   * @example
   * getAuthorizeUrl('page', 'S256');
   * ```
   * 
   */
    public getAuthorizeUrl(displayMode: string, codeChallengeMethod: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.getOpenIDConfiguration(this.authSettings.cxoneHostname).then(
        (data) => {
          const authEndPoint = data.authorizationEndpoint;

          if (!data.displayValuesSupported.find((val) => val === displayMode)) {
            this.logger.error('getAuthorizeUrl', 'Display value not supported');
            reject('Display value not supported');
          }

          if (!data.codeChallengeMethods.find((val) => val === codeChallengeMethod)) {
            this.logger.error('getAuthorizeUrl', 'Code challenge method not supported');
            reject('Code challenge method not supported');
          }

          const verifier = this.securityHelper.generateCodeVerifier();
          LocalStorageHelper.setItem(StorageKeys.CODE_VERIFIER,verifier);

          this.securityHelper
            .generateCodeChallengeFromVerifier(verifier)
            .then((response: string) => {
              const codeChallenge = response;
              const authUrl =
                `${authEndPoint}?client_id=${this.authSettings.clientId}&redirect_uri=${this.authSettings.redirectUri}` +
                `&response_type=code&scope=openid&state=${this.authSettings.state || 'aaxx-11'}&display=${displayMode}&code_challenge=${codeChallenge}&code_challenge_method=&code_challenge_method=${codeChallengeMethod}`;
              resolve(authUrl);
            });
        },
        (error: HttpResponse) => {
          this.logger.error('getAuthorizeUrl', error.toString());
          reject(error);
        }
      );
    });
  }

  /**
   * Method to generate the access token and call the getCXoneConfiguration method in the response
   * to get the api endpoint url from the cxone configuration.
   *
   * @param clientId - 'client id'
   * @param code - 'generated code after authenticate'
   * ```
   * @example
   * getAccessToken('Salesforce Agent Cons..', 'eyJ0eXAiOiJKV1Qi...')
   * ```
   */
  public getAccessToken({clientId, code}: AuthWithCodeReq) : Promise<AuthToken> {
    return new Promise<AuthToken>((resolve, reject) => {
      try {
        const discoveryResponse = LocalStorageHelper.getItem(StorageKeys.OIDC_CONFIG)
        let tokenEndpoint = '';

        if (discoveryResponse) {
          tokenEndpoint = JSON.parse(discoveryResponse).tokenEndpoint;
        } else {
          this.logger.error('getAccessToken', 'Token endpoint is null');
          reject('Token endpoint is null');
        }

        const redirectUrl = window.location.origin;
        const verifier = LocalStorageHelper.getItem(StorageKeys.CODE_VERIFIER);
        const reqInit: HttpRequestInit = {
          headers: this.utilService.initHeader('', 'application/json').headers,
          body: `grant_type=authorization_code&code=${code}&redirect_uri=${redirectUrl}&client_id=${clientId}&code_verifier=${verifier}`,
        };

        HttpClient.post(tokenEndpoint, reqInit).then(
          (resp: HttpResponse) => {
            const authToken = this.parseAndSaveAuthToken(resp);
            this.setAuthEndPoints(authToken);
            this.getWhoAmIData(authToken).then((resp)=>{
              const cxoneConfigObj = this.getCXoneConfig();
              cxoneConfigObj.acdApiBaseUri = resp.resourceServerBaseUri;
              this.setCXoneConfig(cxoneConfigObj);
              LocalStorageHelper.setItem(StorageKeys.CXONE_CONFIG,cxoneConfigObj);
              this.logger.info('getWhoAmIData', 'Successfully fetched and set data'); 
              resolve(authToken);
            })
            this.logger.info('getAccessToken', 'Token generated successfully');
          },
          (error: HttpResponse) => {
            this.logger.error('getAccessToken', error.toString());
            reject(error);
          }
        );
      } catch (error) {
        this.logger.error('getAccessToken', 'error in try catch');
      } finally {
        LocalStorageHelper.removeItem(StorageKeys.CODE_VERIFIER);
      }
    });
  }
}

